import { message, notification } from 'antd'

// THIS IS A GENERAL FUNCTION WHICH HANDLES SETTING FORMVALUES IN FORMDATA, BUT NO FILES INCLUDED.
// IT ASSUMES THAT ALL FILES ARE PRE-UPLOADED
export function appendFormValues(formData, formValues) {
  Object.entries(formValues).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      // Check for non-null and non-undefined values
      if (Array.isArray(value)) {
        // If the value is an array, append each item separately
        value.forEach((item) => {
          if (item !== null && item !== undefined) {
            // Also check each item in the array
            formData.append(`${key}[]`, item)
          }
        })
      } else {
        // Check if the value matches the DD/MM/YYYY date format
        const datePattern =
          /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/(19|20)\d\d$/

        if (typeof value === 'string' && datePattern.test(value)) {
          // Replace '/' with '-' in the date string
          const modifiedDate = value.replace(/\//g, '-')
          formData.set(key, modifiedDate)
        } else {
          // If the value is not an array and does not match the date pattern, set it directly
          formData.set(key, value)
        }
      }
    }
  })
}

export const openNotification = (type, description) => {
  notification.open({
    closeIcon: null,
    description,
    type,
  })
}

export function calculateDiscountPercentage(totalPrice, discountAmount) {
  return (Number(discountAmount) / Number(totalPrice)) * 100
}

export const debounce = (func, wait) => {
  let timeout

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }

    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const DEFAULT_ERROR_MESSAGE = 'Si è verificato un errore'

function firstValidationError(errors) {
  if (!errors || typeof errors !== 'object') return null
  if (Array.isArray(errors)) {
    const first = errors.find(Boolean)
    return typeof first === 'string' ? first : null
  }
  for (const value of Object.values(errors)) {
    if (typeof value === 'string' && value) return value
    if (Array.isArray(value) && value[0]) return String(value[0])
  }
  return null
}

function messageFromErrorData(data) {
  if (!data) return null
  if (typeof data === 'string' && data.trim()) return data
  if (typeof data !== 'object') return null
  if (typeof data.message === 'string' && data.message) return data.message
  return firstValidationError(data.errors)
}

/** Serialize Axios (or similar) errors for rejectWithValue — never store raw AxiosError in Redux. */
export const serializeAxiosError = (error) => ({
  response: {
    status: error?.response?.status ?? error?.status,
    data: error?.response?.data ?? error?.data,
  },
  message: error?.message || DEFAULT_ERROR_MESSAGE,
  status: error?.response?.status ?? error?.status,
  data: error?.response?.data ?? error?.data,
})

export const errorHandling = (error) => {
  if (!error) return DEFAULT_ERROR_MESSAGE

  // RTK Query / axiosBaseQuery normalized: { status, data, message }
  const rtkDataMessage = messageFromErrorData(error.data)
  if (rtkDataMessage) return rtkDataMessage

  // Axios shape: { response: { data, status } }
  const axiosDataMessage = messageFromErrorData(error.response?.data)
  if (axiosDataMessage) return axiosDataMessage

  // Serialized rejectWithValue from serializeAxiosError
  if (error.response && !error.response.data && error.message) {
    return error.message
  }

  if (error.request && !error.response) {
    return `Errore nella richiesta: ${error.message || DEFAULT_ERROR_MESSAGE}`
  }

  if (typeof error.message === 'string' && error.message) {
    return error.message
  }

  return DEFAULT_ERROR_MESSAGE
}

export const getAuthTokenFromCookie = (cookieHeader) => {
  const cookies = cookieHeader.split(';').reduce((acc, cookie) => {
    const [key, value] = cookie.trim().split('=')
    acc[key] = decodeURIComponent(value)
    return acc
  }, {})

  // Replace 'SMARTCAF_USER_JWT_TOKEN' with the actual name of your cookie
  const encodedToken = cookies['SMARTCAF_USER_JWT_TOKEN']
  return encodedToken ? decodeURIComponent(encodedToken) : null
}

export const copyToClipboard = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      // Handle successful copying here, like showing a notification
      message.success('Testo copiato negli appunti')
    })
    .catch((err) => {
      // Handle errors here
      message.error('Failed to copy text: ', err)
    })
}

export function formatFileSize(bytes) {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB']

  // If the size is 0, return 0 Bytes
  if (bytes === 0) return '0 Bytes'

  const index = Math.floor(Math.log(bytes) / Math.log(1024))
  const size = bytes / Math.pow(1024, index)

  // Limiting to 2 decimal places for better readability
  return `${size.toFixed(2)} ${units[index]}`
}

export function isWorkingHours() {
  // Get current date and time in Italy time zone
  const now = new Date().toLocaleString('en-US', { timeZone: 'Europe/Rome' })
  const currentDate = new Date(now)

  // Extract the current day and time
  const day = currentDate.getDay() // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const hours = currentDate.getHours()
  const minutes = currentDate.getMinutes()

  // Check if the day is between Monday (1) and Friday (5)
  const isWeekday = day >= 1 && day <= 5

  // Check if the time is within the working hours: 09:00-13:00 or 14:00-18:00
  const isMorningShift =
    (hours === 9 && minutes >= 0) || (hours >= 10 && hours < 13)
  const isAfternoonShift =
    (hours === 14 && minutes >= 0) || (hours > 14 && hours < 18)

  if (isWeekday && (isMorningShift || isAfternoonShift)) {
    return true
  } else {
    return false
  }
}

// THIS FUNCTION USED IF THE BACKEND RETURNED DATE IN THIS FORMAT 2027-02-11 00:00:00
export function convertDateFormat(datetimeString) {
  if (!datetimeString) return null

  // Extract the date part (before the space)
  const datePart = datetimeString.split(' ')[0]

  // Split the date into its components
  const [year, month, day] = datePart.split('-')

  // Reformat to MM/DD/YYYY
  const formattedDate = `${month}/${day}/${year}`

  return formattedDate
}

// This Function Converts the date string "22-09-2024 21:04:17" into a Date object
export const formatDate = (dateString) => {
  const [datePart, timePart] = dateString.split(' ')
  const [day, month, year] = datePart.split('-')
  const [hours, minutes, seconds] = timePart.split(':')
  const date = new Date(year, month - 1, day, hours, minutes, seconds)

  // Format the date into "12 Apr 2023"
  const formattedDate = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date)

  // Format the time into "5:00 PM"
  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date)

  return { formattedDate, formattedTime }
}

// 2025-04-18T19:16:34.000000Z to 18 aprile 2025, 21:16:34
export function formatDateToItaly(dateString) {
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('it-IT', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(date)
}

export const formatMobileNumber = (number) => {
  return number.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3')
}

export const cleanHTML = (htmlString) => {
  if (htmlString === undefined || htmlString === null) {
    return ''
  }

  if (typeof htmlString !== 'string') {
    return String(htmlString)
  }

  return htmlString
    ?.replaceAll('<p></p>', '')
    ?.replaceAll('<p>&nbsp;</p>', '')
    ?.replaceAll('<h3>&nbsp;</h3>', '')
    ?.replaceAll('<p style="text-align:start;"></p>', '')
    ?.replaceAll('<p style="text-align:left;"><br></p>', '')
    ?.replaceAll('<p style="text-align:left;">&nbsp;</p>', '')
    ?.replaceAll('<h3 style="text-align:start;"></h3>', '')
}

export const formatPrice = (value) => {
  if (value === undefined || value === null) return '0,00'
  return Number(value).toFixed(2).replace('.', ',')
}